/**
 * Card Product Variant Picker
 * Updates price, image and discount when variant is selected in product cards
 */
if (!customElements.get("card-product-variant-picker")) {
  class CardProductVariantPicker extends HTMLElement {
    constructor() {
      super();
      this.variantData = [];
      this.currentVariant = null;
      this.cardPriceElement = null;
      this.cardPriceAmountElement = null;
      this.cardPriceCompareElement = null;
      this.cardPriceBadgeElement = null;
      this.cardImageElement = null;
    }

    connectedCallback() {
      // Check if already initialized to avoid duplicate initialization
      if (this.initialized) {
        return;
      }

      // Use a small delay to ensure DOM is fully loaded
      setTimeout(() => {
        this.getVariantData();
        this.initElements();
        this.bindEvents();
        this.updateSelectedValues();
        this.initAddToCartButton();
        this.initialized = true;
      }, 100);
    }

    getVariantData() {
      const scriptElement = this.querySelector("[data-variant-data]");
      if (scriptElement) {
        try {
          this.variantData = JSON.parse(scriptElement.textContent);
        } catch (error) {
          this.variantData = [];
        }
      } else {
        // Fallback to original method
        const fallbackElement = this.querySelector('[type="application/json"]');
        if (fallbackElement) {
          try {
            this.variantData = JSON.parse(fallbackElement.textContent);
          } catch (error) {
            this.variantData = [];
          }
        } else {
          this.variantData = [];
        }
      }
    }

    initElements() {
      // Find price element within card using specific containers
      const card = this.closest(".card-product");
      if (card) {
        // Look for price container with matching product ID
        const productId = this.dataset.productId;

        // Find the simple price container which contains the card-price element
        const simplePriceContainer = card.querySelector(
          `.card__simple-price[data-product-id="${productId}"]`
        );

        // Try multiple selectors to find the price element
        this.cardPriceElement = simplePriceContainer
          ? simplePriceContainer.querySelector(".card-price")
          : card.querySelector(".card-price");

        // Re-initialize all price elements
        if (this.cardPriceElement) {
          this.cardPriceAmountElement = this.cardPriceElement.querySelector(
            ".card-price__amount"
          );
          this.cardPriceCompareElement = this.cardPriceElement.querySelector(
            ".card-price__compare"
          );
          this.cardPriceBadgeElement =
            this.cardPriceElement.querySelector(".card-price__badge");
        } else {
          // Try alternative selectors
          const priceElements = card.querySelectorAll(".card-price, .price");
          if (priceElements.length > 0) {
            this.cardPriceElement = priceElements[0];
            this.cardPriceAmountElement = this.cardPriceElement.querySelector(
              ".card-price__amount, .price-item--regular, .price-item--sale"
            );
            this.cardPriceCompareElement = this.cardPriceElement.querySelector(
              ".card-price__compare, s.price-item--regular"
            );
            this.cardPriceBadgeElement = this.cardPriceElement.querySelector(
              ".card-price__badge, .price__badge-sale, .price__badge-sold-out"
            );
          }
        }

        // Find the main product image (first-image) within the card
        const firstImageWrapper = card.querySelector(".product-first-image");
        this.cardImageElement = firstImageWrapper
          ? firstImageWrapper.querySelector("img")
          : null;
      }
    }

    bindEvents() {
      // Bind click events directly to all variant buttons
      const buttons = this.querySelectorAll(".card-product__variant-button");

      buttons.forEach((button, index) => {
        button.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (!button.disabled) {
            this.onVariantChange(button);
          }
        });
      });
    }

    updateSelectedValues() {
      const options = Array.from(
        this.querySelectorAll("fieldset"),
        (fieldset) => {
          const activeButton = fieldset.querySelector(
            ".card-product__variant-button.active"
          );
          return activeButton ? activeButton.dataset.optionValue : null;
        }
      );

      // Find the variant that matches all selected options
      this.currentVariant = this.variantData.find((variant) => {
        return variant.options.every((option, index) => {
          return options[index] === option;
        });
      });

      this.updatePrice();
      this.updateButtonStates();
    }

    onVariantChange(button) {
      const fieldset = button.closest("fieldset");

      // Update active state for buttons in this fieldset
      const allButtons = fieldset.querySelectorAll(
        ".card-product__variant-button"
      );

      allButtons.forEach((btn) => {
        btn.classList.remove("active");
      });
      button.classList.add("active");

      // Update selected value display
      const selectedValueSpan = fieldset.querySelector(
        ".card-product__selected-value"
      );
      if (selectedValueSpan) {
        selectedValueSpan.textContent = button.dataset.optionValue;
      }

      this.updateSelectedValues();
    }

    updatePrice() {
      // Update price directly on client side
      this.updateCardPriceClientSide(this.currentVariant);

      // Update image to match the selected variant
      this.updateImage(this.currentVariant);

      // Update product link with variant ID
      const productLink =
        this.closest(".card-product").querySelector(".product-link");
      if (productLink) {
        const currentUrl = new URL(productLink.href);
        currentUrl.searchParams.set("variant", this.currentVariant.id);
        productLink.href = currentUrl.toString();
      }

      // Update variant ID in add to cart form
      const variantInput = this.closest(".card-product").querySelector(
        ".card-product-variant-id"
      );
      if (variantInput) {
        variantInput.value = this.currentVariant.id;
        variantInput.disabled = !this.currentVariant.available;
      }

      // Update add to cart button state
      const addButton = this.closest(".card-product").querySelector(
        ".card-product__add-button"
      );
      if (addButton) {
        addButton.disabled = !this.currentVariant.available;

        const buttonText = addButton.querySelector(
          "span:not(.sold-out-message)"
        );
        const soldOutMessage = addButton.querySelector(".sold-out-message");

        if (this.currentVariant.available) {
          if (buttonText) {
            buttonText.textContent =
              window.theme?.strings?.addToCart || "Añadir al carrito";
          }
          if (soldOutMessage) {
            soldOutMessage.classList.add("hidden");
          }
        } else {
          if (buttonText) {
            buttonText.textContent =
              window.theme?.strings?.soldOut || "Agotado";
          }
          if (soldOutMessage) {
            soldOutMessage.classList.remove("hidden");
          }
        }
      }
    }

    updateImage(variant) {
      if (!this.cardImageElement || !variant || !variant.featured_image) {
        return;
      }

      const src = variant.featured_image.src;
      if (!src) return;

      // src viene protocol-relative desde el JSON, ej: "//www.biostream.cl/cdn/..."
      // Le agregamos los mismos parámetros de tamaño que usa la imagen original
      const newSrc = `${src}?crop=center&height=1254&width=1254`;

      this.cardImageElement.src = newSrc;

      // Quitamos srcset para que el navegador no elija una URL vieja según el ancho de pantalla
      if (this.cardImageElement.hasAttribute("srcset")) {
        this.cardImageElement.removeAttribute("srcset");
      }

      if (variant.featured_image.alt) {
        this.cardImageElement.alt = variant.featured_image.alt;
      }
    }

    updateCardPriceClientSide(variant) {
      const price = variant.price;
      const compareAtPrice = variant.compare_at_price;

      // Re-initialize elements in case DOM structure changed
      if (
        !this.cardPriceAmountElement ||
        !this.cardPriceCompareElement ||
        !this.cardPriceBadgeElement
      ) {
        this.cardPriceAmountElement = this.cardPriceElement.querySelector(
          ".card-price__amount"
        );
        this.cardPriceCompareElement = this.cardPriceElement.querySelector(
          ".card-price__compare"
        );
        this.cardPriceBadgeElement =
          this.cardPriceElement.querySelector(".card-price__badge");
      }

      // Update price amount
      if (this.cardPriceAmountElement) {
        this.cardPriceAmountElement.textContent = this.formatMoney(price);
      } else {
      }

      // Update compare at price
      if (this.cardPriceCompareElement) {
        if (compareAtPrice && compareAtPrice > price) {
          this.cardPriceCompareElement.textContent =
            this.formatMoney(compareAtPrice);
          this.cardPriceCompareElement.style.display = "";
        } else {
          this.cardPriceCompareElement.style.display = "none";
        }
      } else {
      }

      // Update badge
      if (this.cardPriceBadgeElement) {
        if (!variant.available) {
          this.cardPriceBadgeElement.textContent = "Agotado";
          this.cardPriceBadgeElement.className =
            "card-price__badge card-price__badge--sold-out";
          this.cardPriceBadgeElement.style.display = "";
        } else if (compareAtPrice && compareAtPrice > price) {
          const discountPercentage = Math.round(
            ((compareAtPrice - price) * 100) / compareAtPrice
          );
          this.cardPriceBadgeElement.textContent = `-${discountPercentage}%`;
          this.cardPriceBadgeElement.className = "card-price__badge";
          this.cardPriceBadgeElement.style.display = "";
        } else {
          this.cardPriceBadgeElement.style.display = "none";
        }
      } else {
      }

      // Update the entire card price structure if needed
      const container = this.cardPriceElement.querySelector(
        ".card-price__container"
      );
      if (container) {
        // Check if we need to restructure the price display
        const hasSale = compareAtPrice && compareAtPrice > price;
        const saleElement = container.querySelector(".card-price__sale");
        const regularElement = container.querySelector(".card-price__regular");

        if (hasSale && !saleElement) {
          // Need to create sale structure
          container.innerHTML = `
            <span class="card-price__sale">
              <span class="card-price__amount">${this.formatMoney(price)}</span>
              <span class="card-price__compare">${this.formatMoney(
                compareAtPrice
              )}</span>
            </span>
          `;
        } else if (!hasSale && !regularElement) {
          // Need to create regular structure
          container.innerHTML = `
            <span class="card-price__regular">
              <span class="card-price__amount">${this.formatMoney(price)}</span>
            </span>
          `;
        }
      }
    }

    updateButtonStates() {
      const fieldsets = this.querySelectorAll("fieldset");

      fieldsets.forEach((fieldset, index) => {
        const buttons = fieldset.querySelectorAll(
          ".card-product__variant-button"
        );
        const selectedOptions = this.getSelectedOptions();

        buttons.forEach((button) => {
          const optionValue = button.dataset.optionValue;
          const isAvailable = this.isVariantAvailable(
            selectedOptions,
            index,
            optionValue
          );

          button.classList.toggle("disabled", !isAvailable);
          button.disabled = !isAvailable;
        });
      });
    }

    getSelectedOptions() {
      return Array.from(this.querySelectorAll("fieldset")).map((fieldset) => {
        const activeButton = fieldset.querySelector(
          ".card-product__variant-button.active"
        );
        return activeButton ? activeButton.dataset.optionValue : null;
      });
    }

    isVariantAvailable(selectedOptions, currentIndex, currentValue) {
      const testOptions = [...selectedOptions];
      testOptions[currentIndex] = currentValue;

      return this.variantData.some((variant) => {
        return (
          variant.options.every((option, index) => {
            return testOptions[index] === option;
          }) && variant.available
        );
      });
    }

    formatMoney(cents) {
      // Use Shopify's formatMoney function if available, otherwise fallback
      if (window.Shopify && window.Shopify.formatMoney) {
        const formattedPrice = window.Shopify.formatMoney(
          cents,
          theme.settings.money_with_currency_format
        );
        return formattedPrice;
      }

      // Skip vendor.js formatMoney function and use our fallback
      // if (typeof formatMoney === "function") {
      //   const formattedPrice = formatMoney(cents, theme.settings.money_with_currency_format);
      //   console.log("Using vendor.js formatMoney:", formattedPrice);
      //   return formattedPrice;
      // }

      // Basic fallback formatting with correct decimal separator
      const amount = (cents / 100).toFixed(2);
      const parts = amount.split(".");
      const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      const centsPart = parts[1];

      // Only show cents if they're not .00
      const formattedPrice =
        centsPart === "00" ? `$${dollars}` : `$${dollars},${centsPart}`;
      return formattedPrice;
    }

    initAddToCartButton() {
      // Initialize the add to cart button if it exists
      const card = this.closest(".card-product");
      if (card) {
        const productForm = card.querySelector("product-form");
        if (productForm && !productForm.form) {
          const form = card.querySelector(".card-product__add-to-cart form");
          if (form) {
            productForm.form = form;
            const submitButton = form.querySelector('[type="submit"]');
            if (submitButton) {
              productForm.submitButton = submitButton;
            }
            productForm.cart =
              document.querySelector("cart-notification") ||
              document.querySelector("cart-drawer");

            // Add submit event listener
            form.addEventListener(
              "submit",
              productForm.onSubmitHandler.bind(productForm)
            );
          }
        }
      }
    }
  }

  customElements.define(
    "card-product-variant-picker",
    CardProductVariantPicker
  );

  // Initialize variant pickers when they are dynamically added to page
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          // Check if the added node is a variant picker
          if (
            node.classList &&
            node.classList.contains("card-product-variant-picker")
          ) {
            // The browser will automatically initialize custom elements
            // But we need to ensure it's properly initialized
            setTimeout(() => {
              if (node.connectedCallback && !node.initialized) {
                node.connectedCallback();
              }
            }, 100);
          } else if (node.querySelectorAll) {
            const variantPickers = node.querySelectorAll(
              ".card-product-variant-picker"
            );
            variantPickers.forEach((picker) => {
              // The browser will automatically initialize custom elements
              // But we need to ensure it's properly initialized
              setTimeout(() => {
                if (picker.connectedCallback && !picker.initialized) {
                  picker.connectedCallback();
                }
              }, 100);
            });
          }
        }
      });
    });
  });

  // Start observing the document body for added nodes
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  // Also listen for custom events that might be fired when products are loaded
  document.addEventListener("product-recommendations:loaded", function () {
    // Re-initialize all variant pickers
    const variantPickers = document.querySelectorAll(
      ".card-product-variant-picker"
    );
    variantPickers.forEach((picker) => {
      setTimeout(() => {
        if (picker.connectedCallback && !picker.initialized) {
          picker.connectedCallback();
        }
      }, 100);
    });
  });

  // Initialize card product forms when DOM is ready
  document.addEventListener("DOMContentLoaded", function () {
    const cardProductForms = document.querySelectorAll(
      ".card-product__add-to-cart product-form"
    );
    cardProductForms.forEach(function (productForm) {
      if (!productForm.form) {
        const form = productForm.querySelector("form");
        if (form) {
          productForm.form = form;
          const submitButton = form.querySelector('[type="submit"]');
          if (submitButton) {
            productForm.submitButton = submitButton;
          }
          productForm.cart =
            document.querySelector("cart-notification") ||
            document.querySelector("cart-drawer");

          // Add submit event listener
          form.addEventListener(
            "submit",
            productForm.onSubmitHandler.bind(productForm)
          );
        }
      }
    });

    // Initialize variant pickers that might already be in the DOM
    const variantPickers = document.querySelectorAll(
      ".card-product-variant-picker"
    );
    variantPickers.forEach((picker) => {
      setTimeout(() => {
        if (picker.connectedCallback && !picker.initialized) {
          picker.connectedCallback();
        }
      }, 100);
    });
  });
}